import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
} from 'firebase/firestore';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import LayersIcon from '@mui/icons-material/Layers';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Papa from 'papaparse';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

interface Polygon {
  polygonId: string;
  geometry: string;
  criteria: string;
  dateCreated: string;
}

interface Traffic {
  pathId: string;
  month: string;
  volume: number;
  speed: number;
  dwell: number;
}

export interface Asset {
  id: string;
  customId: string;
  operatorCompany: string;
  assetType: string;
  width: number;
  height: number;
  loopSettings: string;
  status: string;
  dateAdded: string;
  latitude: number;
  longitude: number;
  address: string;
  group: string;
  dailyImpression?: number;
  polygon: Polygon;
  traffic: Traffic[];
  facingDirection?: string;
}

const defaultAsset: Asset = {
  id: '',
  customId: '',
  operatorCompany: '',
  assetType: '',
  width: 0,
  height: 0,
  loopSettings: '',
  status: '',
  dateAdded: '',
  latitude: 0,
  longitude: 0,
  address: '',
  group: '',
  dailyImpression: 0,
  polygon: {
    polygonId: '',
    geometry: '',
    criteria: '',
    dateCreated: '',
  },
  traffic: [],
  facingDirection: '',
};

const facingDirections = ['North', 'South', 'East', 'West', 'Northeast', 'Northwest', 'Southeast', 'Southwest'];

const Inventory: React.FC = () => {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [form, setForm] = useState<Asset>(defaultAsset);
  const [loading, setLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState<string[]>([]);
  const [assetGroups, setAssetGroups] = useState<string[]>([]);
  const [operatorCompanies, setOperatorCompanies] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; ids?: string[] }>({ open: false });
  const [confirmEdit, setConfirmEdit] = useState<{ open: boolean; asset?: Asset; index?: number }>({ open: false });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [groupError, setGroupError] = useState('');

  const fetchAssetTypes = async () => {
    const snapshot = await getDocs(collection(db, 'AssetTypes'));
    setAssetTypes(snapshot.docs.map(doc => doc.data().name));
  };
  const fetchAssetGroups = async () => {
    const snapshot = await getDocs(collection(db, 'AssetGroups'));
    setAssetGroups(snapshot.docs.map(doc => doc.data().name));
  };
  const fetchOperatorCompanies = async () => {
    const snapshot = await getDocs(collection(db, 'OperatorCompanies'));
    setOperatorCompanies(snapshot.docs.map(doc => doc.data().name));
  };
  const fetchStatuses = async () => {
    const snapshot = await getDocs(collection(db, 'Statuses'));
    setStatuses(snapshot.docs.map(doc => doc.data().name));
  };

  const fetchAssets = async () => {
    setLoading(true);
    const snapshot = await getDocs(collection(db, 'Assets'));
    const data = snapshot.docs.map(docSnap => ({
      id: docSnap.id,
      customId: docSnap.data().customId || '',
      ...docSnap.data()
    })) as Asset[];
    setAssets(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchAssets();
    fetchAssetTypes();
    fetchAssetGroups();
    fetchOperatorCompanies();
    fetchStatuses();
  }, []);

  const handleOpen = (asset?: Asset, index?: number) => {
    if (asset) {
      setConfirmEdit({ open: true, asset, index });
    } else {
      const now = dayjs().format('YYYY-MM-DDTHH:mm');
      setForm({
        ...defaultAsset,
        dateAdded: now,
        polygon: { ...defaultAsset.polygon, dateCreated: now },
      });
      setEditIndex(null);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setForm(defaultAsset);
    setEditIndex(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let checked = false;
    if (type === 'checkbox' && 'checked' in e.target) {
      checked = (e.target as HTMLInputElement).checked;
    }
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTrafficChange = (idx: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      traffic: prev.traffic.map((t, i) =>
        i === idx ? { ...t, [name]: name === 'volume' || name === 'speed' || name === 'dwell' ? Number(value) : value } : t
      ),
    }));
  };

  const addTraffic = () => {
    setForm(prev => ({ ...prev, traffic: [...prev.traffic, { pathId: '', month: '', volume: 0, speed: 0, dwell: 0 }] }));
  };

  const removeTraffic = (idx: number) => {
    setForm(prev => ({ ...prev, traffic: prev.traffic.filter((_, i) => i !== idx) }));
  };

  const generateUniqueId = async (): Promise<string> => {
    const snapshot = await getDocs(collection(db, 'Assets'));
    const existing = new Set(snapshot.docs.map(docSnap => docSnap.data().id));
    let id = '';
    do {
      id = (Math.floor(100000 + Math.random() * 900000)).toString();
    } while (existing.has(id));
    return id;
  };

  const handleSubmit = async () => {
    // Text inputs yield strings — coerce numeric fields before saving so the
    // map and numeric sorting work correctly.
    const toNum = (v: unknown) => {
      const parsed = parseFloat(String(v ?? '').trim());
      return isNaN(parsed) ? 0 : parsed;
    };
    const normalized: Asset = {
      ...form,
      latitude: toNum(form.latitude),
      longitude: toNum(form.longitude),
      width: toNum(form.width),
      height: toNum(form.height),
      dailyImpression: toNum(form.dailyImpression),
    };
    if (editIndex !== null && form.id) {
      // Update
      const docRef = doc(db, 'Assets', String(form.id));
      await updateDoc(docRef, { ...normalized });
    } else {
      // Add, auto-generate unique 6-digit id, set dateAdded and polygon.dateCreated
      const now = dayjs().format('YYYY-MM-DDTHH:mm');
      const id = await generateUniqueId();
      const docRef = doc(db, 'Assets', id);
      await setDoc(docRef, {
        ...normalized,
        id,
        dateAdded: now,
        polygon: { ...normalized.polygon, dateCreated: now },
      });
    }
    fetchAssets();
    handleClose();
  };

  const handleEditConfirm = () => {
    if (confirmEdit.asset) {
      setForm({
        ...confirmEdit.asset,
        traffic: Array.isArray(confirmEdit.asset.traffic) ? confirmEdit.asset.traffic : [],
      });
      setEditIndex(confirmEdit.index ?? null);
      setOpen(true);
    }
    setConfirmEdit({ open: false });
  };

  const handleEditCancel = () => setConfirmEdit({ open: false });

  const handleDelete = async () => {
    setConfirmDelete({ open: true, ids: selectionModel.map(String) });
  };

  const handleDeleteConfirm = async () => {
    if (confirmDelete.ids && confirmDelete.ids.length > 0) {
      await Promise.all(confirmDelete.ids.map(id => deleteDoc(doc(db, 'Assets', id))));
      fetchAssets();
    }
    setConfirmDelete({ open: false });
  };

  const handleDeleteCancel = () => setConfirmDelete({ open: false });

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      setGroupError('Group name is required');
      return;
    }
    if (selectionModel.length === 0) {
      setGroupError('Select at least one asset');
      return;
    }
    try {
      await addDoc(collection(db, 'InventoryGroups'), {
        name: newGroupName.trim(),
        ids: selectionModel.map(String),
        createdAt: new Date().toISOString(),
      });
      setGroupDialogOpen(false);
      setNewGroupName('');
      setGroupError('');
    } catch (err) {
      setGroupError('Failed to create group');
    }
  };

  const allowedFields = [
    'id', 'customId', 'operatorCompany', 'assetType', 'width', 'height', 'loopSettings',
    'status', 'dateAdded', 'latitude', 'longitude', 'address', 'group', 'dailyImpression',
    'polygon', 'traffic',
    'facingDirection',
  ];

  const csvToFieldMap: Record<string, string> = {
    'Custom ID': 'customId',
    'Operator Company': 'operatorCompany',
    'Type of Inventory': 'assetType',
    'Height': 'height',
    'Width': 'width',
    'Status': 'status',
    'Latitude': 'latitude',
    'Longitude': 'longitude',
    'Facing Direction': 'facingDirection',
    'Address': 'address',
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      complete: async (results: Papa.ParseResult<any>) => {
        const rows = results.data as any[];
        const numericFields = ['latitude', 'longitude', 'width', 'height', 'dailyImpression'];
        for (const row of rows) {
          const cleanInventory: any = {};
          // Map CSV headings to app fields
          Object.entries(csvToFieldMap).forEach(([csvKey, appKey]) => {
            cleanInventory[appKey] = row[csvKey] !== undefined ? row[csvKey] : '';
          });
          // Add missing allowed fields as blank/default
          allowedFields.forEach(field => {
            if (!(field in cleanInventory)) {
              cleanInventory[field] = '';
            }
          });
          // Coerce numeric fields so maps and sorting work (CSV values are strings)
          numericFields.forEach(field => {
            const parsed = parseFloat(String(cleanInventory[field]).trim());
            cleanInventory[field] = isNaN(parsed) ? 0 : parsed;
          });
          // Generate System ID and dateAdded
          const docId = (Math.floor(100000 + Math.random() * 900000)).toString();
          const now = new Date().toISOString();
          await setDoc(doc(db, 'Assets', docId), { ...cleanInventory, id: docId, dateAdded: now });
        }
        fetchAssets(); // Refresh the table
        alert('Upload complete!');
      }
    });
  };

  const exportCsvHeadings = [
    'Custom ID',
    'Operator Company',
    'Type of Inventory',
    'Height',
    'Width',
    'Status',
    'Latitude',
    'Longitude',
    'Facing Direction',
    'Address',
  ];

  const exportCsvMap: Record<string, string> = {
    'Custom ID': 'customId',
    'Operator Company': 'operatorCompany',
    'Type of Inventory': 'assetType',
    'Height': 'height',
    'Width': 'width',
    'Status': 'status',
    'Latitude': 'latitude',
    'Longitude': 'longitude',
    'Facing Direction': 'facingDirection',
    'Address': 'address',
  };

  function exportToCsv(selected: Asset[]) {
    if (!selected.length) return;
    const rows = selected.map(item =>
      exportCsvHeadings.map(h => item[exportCsvMap[h] as keyof Asset] ?? '')
    );
    const csvContent = [
      exportCsvHeadings.join(','),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'inventory_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const columns: GridColDef<Asset>[] = [
    { field: 'id', headerName: 'System ID', width: 120 },
    { field: 'customId', headerName: 'Custom ID', width: 120 },
    { field: 'operatorCompany', headerName: 'Operator', width: 150 },
    { field: 'assetType', headerName: 'Type', width: 120 },
    { field: 'facingDirection', headerName: 'Facing Direction', width: 140 },
    {
      field: 'dimensions',
      headerName: 'Dimensions',
      width: 140,
      renderHeader: () => (
        <span title="Dimensions are in feet (ft). Format: Height x Width.">Dimensions</span>
      ),
      renderCell: (params: GridRenderCellParams<Asset>) => {
        const w = params.row.width;
        const h = params.row.height;
        return (
          <span title="Dimensions are in feet (ft). Format: Height x Width.">
            {(h && w) ? `${h}x${w}` : '-'}
          </span>
        );
      },
      sortable: false,
      filterable: false,
    },
    { field: 'status', headerName: 'Status', width: 120 },
    { field: 'address', headerName: 'Address', width: 200 },
    { field: 'dailyImpression', headerName: 'Daily Impression', width: 150, type: 'number' },
  ];

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 2, ml: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          ADD INVENTORY
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => {
            const idx = assets.findIndex(a => a.id === String(selectionModel[0]));
            if (idx !== -1) handleOpen(assets[idx], idx);
          }}
          disabled={selectionModel.length !== 1}
        >
          EDIT
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          disabled={selectionModel.length === 0}
        >
          REMOVE
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<LayersIcon />}
          onClick={() => setGroupDialogOpen(true)}
          disabled={selectionModel.length === 0}
        >
          CREATE INVENTORY GROUP
        </Button>
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          <input
            type="file"
            accept=".csv"
            id="upload-inventory-input"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <label htmlFor="upload-inventory-input">
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              component="span"
            >
              Upload Inventory
            </Button>
          </label>
        </Box>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={() => exportToCsv(assets.filter(a => selectionModel.includes(a.id)))}
          disabled={selectionModel.length === 0}
        >
          Export Inventory
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          rows={assets.map(a => ({ ...a, id: a.id }))}
          columns={columns}
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick={false}
          onRowSelectionModelChange={model => setSelectionModel(model)}
          rowSelectionModel={selectionModel}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              fontWeight: 'bold',
              fontSize: '1.05rem',
              backgroundColor: 'background.default',
              borderBottom: '2px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editIndex !== null ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              label="Custom ID"
              name="customId"
              value={form.customId}
              onChange={handleChange}
              fullWidth
            />
            <FormControl fullWidth required>
              <InputLabel id="operator-company-label">Operator Company</InputLabel>
              <Select
                labelId="operator-company-label"
                name="operatorCompany"
                value={form.operatorCompany}
                label="Operator Company"
                onChange={e => setForm(prev => ({ ...prev, operatorCompany: e.target.value as string }))}
              >
                {operatorCompanies.map(company => (
                  <MenuItem key={company} value={company}>{company}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel id="asset-type-label">Type</InputLabel>
              <Select
                labelId="asset-type-label"
                name="assetType"
                value={form.assetType}
                label="Type"
                onChange={e => setForm(prev => ({ ...prev, assetType: e.target.value as string }))}
              >
                {assetTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel id="group-label">Group</InputLabel>
              <Select
                labelId="group-label"
                name="group"
                value={form.group}
                label="Group"
                onChange={e => setForm(prev => ({ ...prev, group: e.target.value as string }))}
              >
                {assetGroups.map(group => (
                  <MenuItem key={group} value={group}>{group}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box display="flex" gap={2}>
              <TextField
                label="Width (ft)"
                name="width"
                value={form.width}
                onChange={e => setForm(prev => ({ ...prev, width: Number(e.target.value) }))}
                type="number"
                fullWidth
              />
              <TextField
                label="Height (ft)"
                name="height"
                value={form.height}
                onChange={e => setForm(prev => ({ ...prev, height: Number(e.target.value) }))}
                type="number"
                fullWidth
              />
            </Box>
            <TextField label="Loop Settings" name="loopSettings" value={form.loopSettings} onChange={handleChange} fullWidth />
            <FormControl fullWidth required>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                name="status"
                value={form.status}
                label="Status"
                onChange={e => setForm(prev => ({ ...prev, status: e.target.value as string }))}
              >
                {statuses.map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Date Added" name="dateAdded" value={form.dateAdded} fullWidth InputProps={{ readOnly: true }} type="datetime-local" />
            <TextField label="Latitude" name="latitude" value={form.latitude} onChange={handleChange} fullWidth type="number" />
            <TextField label="Longitude" name="longitude" value={form.longitude} onChange={handleChange} fullWidth type="number" />
            <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth />
            <TextField label="Daily Impression" name="dailyImpression" value={form.dailyImpression} onChange={handleChange} fullWidth type="number" />
            <FormControl fullWidth required>
              <InputLabel id="facing-direction-label">Facing Direction</InputLabel>
              <Select
                labelId="facing-direction-label"
                name="facingDirection"
                value={form.facingDirection}
                label="Facing Direction"
                onChange={e => setForm(prev => ({ ...prev, facingDirection: e.target.value as string }))}
              >
                {facingDirections.map(dir => (
                  <MenuItem key={dir} value={dir}>{dir}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="h6">Traffic Records</Typography>
            {form.traffic.map((t, idx) => (
              <Box key={idx} display="flex" gap={2} alignItems="center">
                <TextField label="Path ID" name="pathId" value={t.pathId} onChange={e => handleTrafficChange(idx, e)} />
                <TextField label="Month" name="month" value={t.month} onChange={e => handleTrafficChange(idx, e)} />
                <TextField label="Volume" name="volume" value={t.volume} onChange={e => handleTrafficChange(idx, e)} type="number" />
                <TextField label="Speed" name="speed" value={t.speed} onChange={e => handleTrafficChange(idx, e)} type="number" />
                <TextField label="Dwell" name="dwell" value={t.dwell} onChange={e => handleTrafficChange(idx, e)} type="number" />
                <IconButton onClick={() => removeTraffic(idx)}><DeleteIcon /></IconButton>
              </Box>
            ))}
            <Button onClick={addTraffic} startIcon={<AddIcon />}>Add Traffic</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editIndex !== null ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDelete.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete {confirmDelete.ids?.length || 0} selected item(s)?</DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmEdit.open} onClose={handleEditCancel}>
        <DialogTitle>Confirm Edit</DialogTitle>
        <DialogContent>Are you sure you want to edit this item?</DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button onClick={handleEditConfirm} color="primary">Edit</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)}>
        <DialogTitle>Create Inventory Group</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Group Name"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              autoFocus
              fullWidth
              error={!!groupError}
              helperText={groupError}
            />
            <Typography variant="body2">
              {selectionModel.length} asset(s) will be added to this group.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateGroup} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inventory; 